import type { TypeRequestReason } from '../../../request/types';
import type { TypeUploadBody } from '../../types';

import type { TypeUploadConfig, TypeUploadOnceResult, TypeResolvedFeatures } from '../types';
import type { TypeUploadContext } from '../createUploadContext';
import type { TypeRequestRegistry } from '../createRequestRegistry';
import type { TypeLiveRequest, TypeRequestHooks, TypeUploadFeature } from './types';
import { mapReason } from '../utils';

import * as exRequest from '../../../request/xhr';

// ----------------------------------------------------------------------------

export type TypeSendRequestDeps = {
    config: TypeUploadConfig;
    features: TypeResolvedFeatures;
    plugins: TypeUploadFeature[];
    context: TypeUploadContext;
    registry: TypeRequestRegistry;
    body: TypeUploadBody;
    // Extra headers for this one request, on top of whatever `config.xhr` carries.
    headers?: Record<string, string>;
    // What this request is carrying, surfaced in the messages features produce.
    label: string;
    // The caller's own progress accounting, run after the feature hooks have seen the event.
    fnUploadProgress?: (evt: ProgressEvent) => void;
    // Runs on a successful response, before the result is resolved.
    fnSuccess?: (xhr: XMLHttpRequest) => void;
};

// ----------------------------------------------------------------------------

/*
 * One request, one attempt. This is the whole transport: it owns the XHR lifecycle, the registry
 * bookkeeping and the abort/cancellation distinction, and nothing else. Every optional behaviour
 * around it — stalls, retries, timeouts — is a feature, either through the request hooks below or
 * by wrapping this call.
 *
 * It neither throws nor retries: the outcome comes back as a TypeUploadOnceResult, with
 * `retryable` set for failures that can never succeed.
 */
async function sendRequest(deps: TypeSendRequestDeps): Promise<TypeUploadOnceResult> {
    const { config, features, plugins, context, registry, body } = deps;

    if (context.abortedFlag.aborted) {
        return { ok: false, status: 0, reason: 'abort' };
    }

    const hooks: TypeRequestHooks[] = [];

    for (const plugin of plugins) {
        if (plugin.request) {
            hooks.push(plugin.request);
        }
    }

    return new Promise<TypeUploadOnceResult>((resolve): void => {
        let xhrRef: XMLHttpRequest | null = null;
        let live: TypeLiveRequest | null = null;
        let isSettled = false;

        /*
         * Set when a feature ends the request itself. The browser reports that as an abort, so
         * without this the outcome would read as a cancellation instead of the transport failure
         * it is — and the retry policy would stop rather than try again.
         */
        let failure: { reason: TypeRequestReason; message: string } | null = null;

        const fnResolveOnce = (result: TypeUploadOnceResult): void => {
            if (isSettled) {
                return;
            }

            isSettled = true;

            // Always fires, on every outcome, so a feature holding a timer can release it.
            if (live) {
                for (const hook of hooks) {
                    hook.onSettle?.(live);
                }
            }

            resolve(result);
        };

        const fnResolveAbort = (xhrInstance?: XMLHttpRequest, status?: number): void => {
            fnResolveOnce({
                ok: false,
                xhr: xhrInstance,
                status: status ?? xhrInstance?.status ?? 0,
                reason: 'abort',
            });
        };

        const xhr = exRequest.xhr({
            /*
             * Folded into the merge rather than passed as a plain key: `merge` outranks plain keys
             * in the request layer, so a caller's `xhr` would otherwise drop these headers.
             */
            merge: deps.headers ? { ...config.xhr, headers: { ...config.xhr?.headers, ...deps.headers } } : config.xhr,
            url: config.url,
            method: config.method ?? 'POST',
            autoSend: false,
            timeout: features.timeout.enabled ? features.timeout.ms : undefined,
            fnProgressUpload: (evt): void => {
                if (!evt.lengthComputable) {
                    return;
                }

                if (live) {
                    for (const hook of hooks) {
                        hook.onUploadProgress?.(live, evt);
                    }
                }

                deps.fnUploadProgress?.(evt);
            },
            fnSuccess: (data, reason, xhrInstance): void => {
                registry.remove(xhrInstance);

                deps.fnSuccess?.(xhrInstance);
                config.fnSuccess?.(data, reason, xhrInstance);

                fnResolveOnce({ ok: true, xhr: xhrInstance, status: xhrInstance.status, reason: 'success' });
            },
            fnFail: (xhrInstance, reason): void => {
                registry.remove(xhrInstance);

                if (registry.isAborted(xhrInstance) || context.abortedFlag.aborted) {
                    fnResolveAbort(xhrInstance);
                    return;
                }

                config.fnFail?.(xhrInstance, reason);

                fnResolveOnce({ ok: false, xhr: xhrInstance, status: xhrInstance.status, reason: 'failed' });
            },
            fnError: (evt, reason): void => {
                const xhrStatus = xhrRef?.status ?? 0;

                if (xhrRef) {
                    registry.remove(xhrRef);
                }

                if ((xhrRef && registry.isAborted(xhrRef)) || context.abortedFlag.aborted) {
                    fnResolveAbort(xhrRef ?? undefined, xhrStatus);
                    return;
                }

                config.fnError?.(evt, reason);

                fnResolveOnce({
                    ok: false,
                    xhr: xhrRef ?? undefined,
                    status: xhrStatus,
                    reason: mapReason(reason),
                });
            },
            fnAbort: (): void => {
                if (xhrRef) {
                    registry.remove(xhrRef);
                }

                // A feature that ended the request aborted it too, but that is not a cancellation.
                if (failure) {
                    config.fnError?.(null, failure.reason);

                    fnResolveOnce({
                        ok: false,
                        xhr: xhrRef ?? undefined,
                        status: 0,
                        reason: failure.reason,
                        message: failure.message,
                    });
                    return;
                }

                fnResolveAbort(xhrRef ?? undefined);
            },
            fnTimeout: (evt): void => {
                const xhrStatus = xhrRef?.status ?? 0;

                if (xhrRef) {
                    registry.remove(xhrRef);
                }

                config.fnError?.(evt, 'timeout');

                fnResolveOnce({ ok: false, xhr: xhrRef ?? undefined, status: xhrStatus, reason: 'timeout' });
            },
        });

        xhrRef = xhr;
        live = {
            xhr,
            label: deps.label,
            fail: (reason, message): void => {
                if (isSettled) {
                    return;
                }

                failure = { reason, message };

                try {
                    xhr.abort();
                } catch {
                    // ignore
                }
            },
        };

        registry.register(xhr);

        if (context.abortedFlag.aborted) {
            registry.markAborted(xhr);

            try {
                xhr.abort();
            } catch {
                // ignore
            }

            registry.remove(xhr);
            fnResolveAbort(xhr);
            return;
        }

        xhr.send(body as XMLHttpRequestBodyInit);

        /*
         * After send, so a feature bounding the connection covers a request that never reports any
         * progress. Skipped when send() already settled it synchronously — starting a timer then
         * would leave one nothing clears, which later aborts a finished request.
         */
        if (!isSettled) {
            for (const hook of hooks) {
                hook.onStart?.(live);
            }
        }
    });
}

// ----------------------------------------------------------------------------

export default sendRequest;
